import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getCargoCompanies,
  getCargoCompany,
  getCargoCompanyDetail,
  createCargoCompany,
  updateCargoCompany,
  deleteCargoCompany,
  getCargoCompanyUsers,
  assignUserToCargoCompany,
  removeUserFromCargoCompany,
} from "../services/cargoCompanyService";
import {
  CargoCompany,
  CargoCompanyDetail,
  CargoCompanyUser,
  CreateCargoCompanyRequest,
  UpdateCargoCompanyRequest,
  AssignUserToCargoCompanyRequest,
  CargoCompanyFilters,
} from "../types/cargoCompany";

// Query Keys for consistent caching
export const cargoCompanyKeys = {
  all: ["cargo-companies"] as const,
  list: (filters?: CargoCompanyFilters) =>
    [...cargoCompanyKeys.all, "list", filters] as const,
  detail: (id: string) => [...cargoCompanyKeys.all, "detail", id] as const,
  users: (id: string, activeOnly?: boolean) =>
    [...cargoCompanyKeys.all, "users", id, activeOnly] as const,
};

// Hook for fetching cargo companies
export const useCargoCompanies = (filters?: CargoCompanyFilters) => {
  return useQuery({
    queryKey: cargoCompanyKeys.list(filters),
    queryFn: () => getCargoCompanies(filters),
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for fetching cargo company by ID
export const useCargoCompany = (id: string, enabled = true) => {
  return useQuery({
    queryKey: cargoCompanyKeys.detail(id),
    queryFn: () => getCargoCompany(id),
    enabled: enabled && !!id,
    staleTime: 30000, // 30 seconds
  });
};

// Hook for fetching cargo company detail (with users)
export const useCargoCompanyDetail = (id: string, enabled = true) => {
  return useQuery({
    queryKey: [...cargoCompanyKeys.detail(id), "full"],
    queryFn: () => getCargoCompanyDetail(id),
    enabled: enabled && !!id,
    staleTime: 30000, // 30 seconds
  });
};

// Hook for fetching cargo company users
export const useCargoCompanyUsers = (
  id: string,
  activeOnly = true,
  enabled = true,
) => {
  return useQuery({
    queryKey: cargoCompanyKeys.users(id, activeOnly),
    queryFn: () => getCargoCompanyUsers(id, activeOnly),
    enabled: enabled && !!id,
    staleTime: 30000, // 30 seconds
  });
};

// Hook for cargo company mutations
export const useCargoCompanyMutations = () => {
  const queryClient = useQueryClient();

  // Create cargo company mutation
  const createMutation = useMutation({
    mutationFn: createCargoCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cargoCompanyKeys.all });
    },
    onError: (error) => {
      console.error("Error creating cargo company:", error);
    },
  });

  // Update cargo company mutation
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateCargoCompanyRequest;
    }) => updateCargoCompany(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: cargoCompanyKeys.all });
      queryClient.invalidateQueries({
        queryKey: cargoCompanyKeys.detail(variables.id),
      });
    },
    onError: (error) => {
      console.error("Error updating cargo company:", error);
    },
  });

  // Delete cargo company mutation
  const deleteMutation = useMutation({
    mutationFn: deleteCargoCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cargoCompanyKeys.all });
    },
    onError: (error) => {
      console.error("Error deleting cargo company:", error);
    },
  });

  // Assign user mutation
  const assignUserMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: AssignUserToCargoCompanyRequest;
    }) => assignUserToCargoCompany(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: cargoCompanyKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: cargoCompanyKeys.users(variables.id),
      });
    },
    onError: (error) => {
      console.error("Error assigning user to cargo company:", error);
    },
  });

  // Remove user mutation
  const removeUserMutation = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      removeUserFromCargoCompany(id, userId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: cargoCompanyKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: cargoCompanyKeys.users(variables.id),
      });
    },
    onError: (error) => {
      console.error("Error removing user from cargo company:", error);
    },
  });

  return {
    create: createMutation,
    update: updateMutation,
    delete: deleteMutation,
    assignUser: assignUserMutation,
    removeUser: removeUserMutation,
  };
};

